import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import Logo from "../components/Logo";
import { ArrowRight, Sparkles, Eye, Flame, MessageSquare, Users2, Swords, LayoutTemplate } from "lucide-react";

const KIND_META = {
  debate:       { label: "Debate",       icon: Swords,         color: "#EF4444" },
  conversation: { label: "Conversation", icon: MessageSquare,  color: "#3B82F6" },
  template:     { label: "Worker",       icon: Users2,         color: "#F59E0B" },
};

export default function Showcase() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/showcase`, { withCredentials: false })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load showcase"));
  }, []);

  const list = (() => {
    if (!data) return [];
    if (tab === "all") return data.featured || [];
    if (tab === "trending") return data.trending || [];
    return (data[tab] || []);
  })();

  return (
    <div className="min-h-screen bg-[#FDF8F5]">
      {/* Top bar */}
      <header className="px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/"><Logo size={36} /></Link>
        <div className="flex items-center gap-3">
          <Link to="/models" className="text-sm font-heading font-bold text-slate-700 hover:text-slate-900">
            12 Brains
          </Link>
          <Link to="/login" className="brut-btn text-sm" data-testid="showcase-signup">
            Try AiMedley <ArrowRight size={12} strokeWidth={2.5} />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-6 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-4">
          <Sparkles size={12} strokeWidth={2.5} /> Public Showcase
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-3" data-testid="showcase-title">
          Real workers.  <span className="bg-amber-200 px-2 border-2 border-slate-900 inline-block rotate-[-1deg]">Real debates.</span>
        </h1>
        <p className="text-base text-slate-600 max-w-2xl mx-auto">
          A live gallery of AI conversations, model debates, and worker templates our community has built.
          No signup — just click any card to watch it play out, then remix it in your own workspace.
        </p>
      </section>

      {/* Section tabs */}
      <div className="max-w-6xl mx-auto px-6 mb-6">
        <div className="flex flex-wrap gap-2" data-testid="showcase-tabs">
          <TabBtn active={tab === "all"}           onClick={() => setTab("all")}           icon={Sparkles} label={`Featured (${data?.counts?.featured || 0})`} testId="tab-all" />
          <TabBtn active={tab === "trending"}      onClick={() => setTab("trending")}      icon={Flame}    label={`Trending (${data?.counts?.trending || 0})`} testId="tab-trending" />
          <TabBtn active={tab === "debates"}       onClick={() => setTab("debates")}       icon={Swords}   label={`Debates (${data?.debates?.length || 0})`} testId="tab-debates" />
          <TabBtn active={tab === "conversations"} onClick={() => setTab("conversations")} icon={MessageSquare} label={`Chats (${data?.conversations?.length || 0})`} testId="tab-conversations" />
          <TabBtn active={tab === "templates"}     onClick={() => setTab("templates")}     icon={LayoutTemplate} label={`Workers (${data?.templates?.length || 0})`} testId="tab-templates" />
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-6 pb-20">
        {err && <div className="brut-card p-4 text-sm text-red-600">{err}</div>}
        {!data && !err && <div className="text-slate-400 italic text-sm">Loading gallery…</div>}
        {data && list.length === 0 && (
          <div className="brut-card p-8 text-center">
            <div className="text-4xl mb-2">🌱</div>
            <div className="font-heading font-bold text-lg mb-1">Nothing curated here yet</div>
            <p className="text-sm text-slate-600">Ship a debate, share the link, and mark it as Featured to see it here.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="showcase-grid">
          {list.map((item) => <ShowcaseCard key={item.share_id} item={item} />)}
        </div>

        {/* CTA */}
        <div className="brut-card p-6 md:p-8 mt-10 text-center relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-amber-200 opacity-40" />
          <div className="relative">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Your turn</div>
            <h3 className="font-heading text-2xl font-bold mb-2">Build workers that never leave.</h3>
            <p className="text-slate-600 max-w-md mx-auto mb-4">
              Pick a brain, give it a mission, and share your first result in under 5 minutes.
            </p>
            <Link to="/login" className="brut-btn" data-testid="showcase-cta-bottom">
              <Sparkles size={14} strokeWidth={2.5} /> Start free
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-slate-900 font-heading font-bold text-xs uppercase tracking-widest transition-colors ${active ? "bg-slate-900 text-white" : "bg-white text-slate-900 hover:bg-amber-50"}`}
      data-testid={testId}
    >
      <Icon size={12} strokeWidth={2.5} /> {label}
    </button>
  );
}

function ShowcaseCard({ item }) {
  const meta = KIND_META[item.kind] || KIND_META.conversation;
  const KindIcon = meta.icon;
  return (
    <Link
      to={`/share/${item.share_id}`}
      className="brut-card p-4 group hover:-translate-y-1 hover:translate-x-0 transition-transform relative overflow-hidden"
      data-testid={`card-${item.share_id}`}
      style={{ boxShadow: `4px 4px 0 ${meta.color}` }}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-14 h-14 rounded-2xl border-2 border-slate-900 flex items-center justify-center text-3xl shrink-0"
          style={{ backgroundColor: meta.color + "22" }}
        >
          {item.icon || "✨"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-heading font-bold px-1.5 py-0.5 rounded-full border-2 mb-1"
               style={{ borderColor: meta.color, color: meta.color, backgroundColor: meta.color + "1F" }}>
            <KindIcon size={10} strokeWidth={2.5} /> {meta.label}
          </div>
          <div className="font-heading font-bold text-base leading-tight line-clamp-2">{item.title}</div>
          {item.author && (
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-0.5">by {item.author}</div>
          )}
        </div>
      </div>
      {item.blurb && <p className="text-xs text-slate-600 line-clamp-2 mb-3">{item.blurb}</p>}
      <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono">
        <span className="inline-flex items-center gap-1"><Eye size={10} /> {item.view_count} view{item.view_count === 1 ? "" : "s"}</span>
        <span className="font-heading font-bold text-slate-900 uppercase tracking-widest group-hover:text-amber-600">
          Open →
        </span>
      </div>
    </Link>
  );
}
